import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

// Types
import { User } from 'Shared/constants/types';

// Selectors
import { getAllBaInfo } from 'Shared/selectors/BaInfo.selectors';

// Actions
import * as PiIntegrationsActions from 'Shared/actions/PiIntegrations.actions';

// Hooks
import { useOrganization } from './use-organization';
import { useCurrentUser } from './use-current-user';

// Utils
import { sortUsersByName } from 'Shared/utils/sorting';

export enum BAStatus {
  Unknown = 'unknown',
  Pending = 'pending',
  Available = 'available',
}

export const useFetchBulkBAData = (users: User[]) => {
  const dispatch = useDispatch<ThunkDispatch<{}, {}, AnyAction>>();
  const { id: currentUserId } = useCurrentUser();
  const { organization } = useOrganization();
  const allBaInfo = useSelector(getAllBaInfo);

  const [state, setState] = useState({
    usersWithBA: [],
    usersWithPendingBA: [],
    usersWithoutBA: [],
    isLoading: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [isFetchingBAData, setIsFetchingBAData] = useState(false);

  const allUsers = useMemo(() => [...state.usersWithPendingBA, ...state.usersWithBA, ...state.usersWithoutBA], [
    state.usersWithPendingBA,
    state.usersWithBA,
    state.usersWithoutBA,
  ]);

  const determineBAStatus = (hasPendingBa: boolean, referencePatternName?: string): BAStatus => {
    if (hasPendingBa && !referencePatternName) {
      return BAStatus.Pending;
    } else if (referencePatternName) {
      return BAStatus.Available;
    } else {
      return BAStatus.Unknown;
    }
  };

  const filterBaStatus = async (user: User) => {
    const baInfo = allBaInfo.get(user.piUserGuid)?.toJS();

    if (baInfo) {
      return { status: determineBAStatus(baInfo.hasPendingBa, baInfo.referencePattern?.referencePatternName), user };
    } else {
      try {
        const response = await dispatch(
          PiIntegrationsActions.getUser({
            companyGuid: organization.pi_company_guid,
            userGuid: user.piUserGuid,
          })
        );
        const responseData = response?.data;
        return {
          status: determineBAStatus(responseData?.hasPendingBa, responseData?.referencePattern?.referencePatternName),
          user,
        };
      } catch (error) {
        console.error(`Error fetching user data for userGuid: ${user.piUserGuid}`, error);
        return { status: BAStatus.Unknown, user };
      }
    }
  };

  const sortAndOrganizeUsers = (users: User[], currentUserId: number) => {
    const sortedUsers = sortUsersByName(users);
    const currentUserIndex = sortedUsers.findIndex(user => user.id === currentUserId);
    if (currentUserIndex > -1) {
      const [currentUser] = sortedUsers.splice(currentUserIndex, 1);
      sortedUsers.unshift(currentUser);
    }
    return sortedUsers;
  };

  useEffect(() => {
    if (isFetchingBAData || users.length === 0) {
      return;
    }

    setIsFetchingBAData(true);
    setState(prev => ({ ...prev, isLoading: true }));
    setError(null);

    const fetchBAData = async () => {
      try {
        const results = await Promise.all(users.map(filterBaStatus));
        const usersWithBA = results.filter(r => r.status === BAStatus.Available).map(r => r.user);
        const usersWithPendingBA = results.filter(r => r.status === BAStatus.Pending).map(r => r.user);
        const usersWithoutBA = results.filter(r => r.status === BAStatus.Unknown).map(r => r.user);

        setState({
          usersWithBA: sortAndOrganizeUsers(usersWithBA, currentUserId),
          usersWithPendingBA: sortUsersByName(usersWithPendingBA),
          usersWithoutBA: sortUsersByName(usersWithoutBA),
          isLoading: false,
        });
      } catch (error) {
        console.error('Error fetching BA data:', error);
        setError('Failed to fetch BA data');
      } finally {
        setIsFetchingBAData(false);
      }
    };

    fetchBAData();
  }, [users, currentUserId]);

  return {
    ...state,
    allUsers,
    error,
    isFetchingBAData,
  };
};
